<template>
  <main class="main-page">
    <h1 class="hidden-header">{{ pageHeader }}</h1>
    <template v-for="(block, index) in pageContent" :key="block.id">
      <Component
        :is="articleBlocks[block.type || block.resourcetype]"
        v-if="articleBlocks[block.type || block.resourcetype]"
        :page-header="pageHeader"
        invert-tabs
        v-bind="getBlocksProps(block, index)"
      />
    </template>
  </main>
</template>

<script setup lang="ts">
import {
  createError,
  ref,
  useArticleBlocks,
  useAsyncData,
  useHead,
  useRouter,
  useRuntimeConfig,
  useSeoMeta,
} from '#imports';
import { type ApiPageExtendedResponse, type PageBlocks } from '~/types/pages';
import './styles.scss';
import type { ErrorResponse } from '~/server/utils/serverFetch';
import { isErrorResponse } from '~/server/utils/serverError';
import { useSchemaOrgPage } from '~/composables/useSchemaOrgPage';
import CBannerMainPage from '~/components/blocks/mainBannerPage/CBannerMainPage.vue';
import CBlockTabs from '~/components/blocks/tabsBlock/CBlockTabs.vue';
import CServiceCatalogCategoryBlock from '~/components/blocks/serviceCatalogCategoryBlock/CServiceCatalogCategoryBlock.vue';

const pageContent = ref<PageBlocks[]>();
const pageHeader = ref<string>();
const route = useRouter().currentRoute.value;

const { articleBlocks, getBlocksProps } = useArticleBlocks();
const { siteUrl } = useRuntimeConfig().public;

const { data, error } = await useAsyncData<ErrorResponse | ApiPageExtendedResponse>(
  'getIndexResponse',
  () => {
    return $fetch(`/api/pages/extended`, {
      method: 'GET',
      params: {
        slug: 'index',
      },
    });
  },
);

const emit = defineEmits(['error-not-found']);

if (error.value) {
  emit('error-not-found');
}

const mainBannerBlock = ref([]);
const mainTabsBlock = ref([]);
const mainServicesBlock = ref([]);
const otherBlocks = ref([]);

if (data.value) {
  if (!isErrorResponse(data.value)) {
    pageContent.value = data.value.blocks;

    pageHeader.value = data.value.seo.h1;

    useHead({
      title: data.value.seo.title || data.value.title,
    });

    useSeoMeta({
      title: data.value.seo.title.replace(/[""'`']/g, ''),
      description: data.value.seo.description.replace(/[""'`']/g, ''),
      keywords: data.value.seo.keywords.replace(/[""'`']/g, ''),
      ogTitle: data.value.og.title.replace(/[""'`']/g, ''),
      ogDescription: data.value.og.description.replace(/[""'`']/g, ''),
      ogImage: data.value?.og.image.source
        ? `${siteUrl}${data.value.og.image.source}`
        : '/android-chrome-192x192.png',
      ogUrl: route.fullPath,
    });

    useSchemaOrgPage({
      template: 'unique-placeholder',
      title: data.value.seo.title || data.value.title,
      description: data.value.seo.description,
    });
  } else {
    emit('error-not-found');
  }
}
</script>
